import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Filter } from 'lucide-react';

import { Input } from '@repo/ui/components/ui/input';
import { Button } from '@repo/ui/components/ui/button';
import { Skeleton } from '@repo/ui/components/ui/skeleton';
import { usePhotographers } from '@/state/use-photographers';
import type { Photographer } from '@/services/photovogue';
import { PhotographerTable } from '@/components/photovogue/photographer-table/component';
import { PhotographerFilterPanel } from '@/components/photovogue/photographer-filter-panel/component';
import { PaginationControls } from '@/components/photovogue/photo-pagination/component';

export const PhotographerList = () => {
  const {
    data,
    count,
    error,
    loadingSpinner,
    updatingIds,
    params,
    init,
    updateFilters,
    updatePagination,
    updateSort,
    hasActiveFilters,
    resetFilters,
    moderatePhotographer,
    updateSpotlight,
    deletePhotographer,
  } = usePhotographers();
  const navigate = useNavigate();
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    init();
  }, [init]);

  const onRowClick = (photographer: Photographer) =>
    navigate(`/photovogue/admin/photographers/${photographer.id}`);

  return (
    <section>
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">Photographers</h1>
        <p className="text-sm text-cn-muted-foreground">
          Manage photographers and review their content.
        </p>
      </header>

      <div className="mb-4 flex items-center gap-3">
        <Input
          placeholder="Search photographers by name…"
          value={params.filters.search ?? ''}
          onChange={(event) =>
            updateFilters(
              (filters) => ({
                ...filters,
                search: event.target.value || undefined,
              }),
              true,
            )
          }
          className="max-w-sm"
        />
        <span className="text-sm text-cn-muted-foreground">
          {count} photographer{count === 1 ? '' : 's'}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setFiltersOpen(true)}
          >
            <Filter /> Filters
          </Button>
          {hasActiveFilters() && (
            <Button size="sm" variant="ghost" onClick={() => resetFilters()}>
              Clear filters
            </Button>
          )}
          <select
            value={`${params.sort.field}:${params.sort.direction}`}
            onChange={(event) => {
              const [field, direction] = event.target.value.split(':') as [
                'name' | 'totalPhotos',
                'asc' | 'desc',
              ];
              updateSort({ field, direction });
            }}
            className="h-8 rounded-md border border-input bg-transparent px-2 text-xs"
            aria-label="Sort photographers"
          >
            <option value="name:asc">Name A–Z</option>
            <option value="name:desc">Name Z–A</option>
            <option value="totalPhotos:desc">Most photos</option>
            <option value="totalPhotos:asc">Fewest photos</option>
          </select>
        </div>
      </div>

      {loadingSpinner ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : error ? (
        <div
          role="alert"
          className="rounded-lg border border-destructive/40 bg-destructive/5 px-6 py-10 text-center"
        >
          <p className="text-sm font-medium">Unable to load photographers.</p>
          <p className="mt-1 text-xs text-cn-muted-foreground">
            The current count is unavailable. Try the request again.
          </p>
          <Button className="mt-4" size="sm" onClick={() => init()}>
            Retry
          </Button>
        </div>
      ) : count === 0 ? (
        <div className="rounded-lg border border-dashed bg-white py-16 text-center text-sm text-cn-muted-foreground">
          No photographers found.
        </div>
      ) : (
        <>
          <PhotographerTable
            photographers={data}
            updatingIds={updatingIds}
            onRowClick={onRowClick}
            onStateChange={moderatePhotographer}
            onSpotlightChange={updateSpotlight}
            onDelete={deletePhotographer}
          />
          <PaginationControls
            count={count}
            page={params.pagination.page}
            limit={params.pagination.limit}
            onPageChange={(page) =>
              updatePagination((pagination) => ({ ...pagination, page }))
            }
          />
        </>
      )}

      <PhotographerFilterPanel
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        filters={params.filters}
        updateFilters={updateFilters}
        resetFilters={resetFilters}
      />
    </section>
  );
};

export default PhotographerList;
